import React from 'react';
import Card from '../Card';
import MetricCardGrid from '../MetricCardGrid';
import ProgressBar from '../ProgressBar';
import { ProjectMetrics, TaskMetrics } from '../../utils/reportingUtils';
import { getPriorityColor } from '../../utils/reportingUtils';

interface OverviewTabProps {
  projectMetrics: ProjectMetrics;
  taskMetrics: TaskMetrics;
}

const OverviewTab: React.FC<OverviewTabProps> = ({ projectMetrics, taskMetrics }) => {
  const summaryMetrics = [
    {
      value: projectMetrics.totalProjects,
      label: 'Total Projects',
      color: 'primary' as const
    },
    {
      value: taskMetrics.totalTasks,
      label: 'Total Tasks',
      color: 'primary' as const
    },
    {
      value: taskMetrics.completedTasks,
      label: 'Completed Tasks',
      color: 'primary' as const
    },
    {
      value: taskMetrics.overdueTasks,
      label: 'Overdue Tasks',
      color: taskMetrics.overdueTasks > 0 ? ('danger' as const) : ('success' as const)
    }
  ];

  const completionPercentage = taskMetrics.totalTasks > 0 
    ? Math.round((taskMetrics.completedTasks / taskMetrics.totalTasks) * 100)
    : 0;

  return (
    <div>
      <h2 className="text-2xl font-semibold text-gray-800 dark:text-gray-200 mb-6">Project Status Dashboard</h2>

      <MetricCardGrid metrics={summaryMetrics} columns={4} className="mb-8" />

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        <Card>
          <Card.Body>
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4">Tasks by Status</h3>
            {Object.entries(taskMetrics.tasksByStatus).map(([status, count]) => (
              <div key={status} className="mb-2 flex justify-between items-center">
                <span className="text-gray-700 dark:text-gray-300 capitalize">{status.replace('_', ' ')}</span>
                <span className="px-2 py-1 text-xs font-semibold rounded-full bg-primary-100 dark:bg-primary-900 text-primary-800 dark:text-primary-200">{count}</span>
              </div>
            ))}
          </Card.Body>
        </Card>

        <Card>
          <Card.Body>
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4">Tasks by Priority</h3>
            {Object.entries(taskMetrics.tasksByPriority).map(([priority, count]) => (
              <div key={priority} className="mb-2 flex justify-between items-center">
                <div className="flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full" style={{ backgroundColor: getPriorityColor(priority) }}></div>
                  <span className="text-gray-700 dark:text-gray-300 capitalize">{priority}</span>
                </div>
                <span className="px-2 py-1 text-xs font-semibold rounded-full bg-primary-100 dark:bg-primary-900 text-primary-800 dark:text-primary-200">{count}</span>
              </div>
            ))}
          </Card.Body>
        </Card>

        <Card>
          <Card.Body>
            <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4">Project Statistics</h3>
            <div className="mb-2 flex justify-between">
              <span className="text-gray-700 dark:text-gray-300">Projects with tasks</span>
              <span className="font-bold text-gray-900 dark:text-gray-100">{projectMetrics.projectsWithTasks}</span>
            </div>
            <div className="mb-2 flex justify-between">
              <span className="text-gray-700 dark:text-gray-300">Projects without tasks</span>
              <span className={`font-bold ${projectMetrics.projectsWithoutTasks > 0 ? 'text-error' : 'text-gray-600 dark:text-gray-400'}`}>
                {projectMetrics.projectsWithoutTasks}
              </span>
            </div>
            <div className="mb-2 flex justify-between">
              <span className="text-gray-700 dark:text-gray-300">Unassigned tasks</span>
              <span className={`font-bold ${taskMetrics.unassignedTasks > 0 ? 'text-error' : 'text-gray-600 dark:text-gray-400'}`}>
                {taskMetrics.unassignedTasks}
              </span>
            </div>
          </Card.Body>
        </Card>
      </div>

      <Card className="mt-6">
        <Card.Body>
          <h3 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-4">Completion Rate</h3>
          <div className="text-4xl font-bold text-success mb-2">
            {completionPercentage}%
          </div>
          <ProgressBar percentage={completionPercentage} color="success" height="lg" />
        </Card.Body>
      </Card>
    </div>
  );
};

export default OverviewTab;
